import { useState, useEffect } from "react";
import SEO from "../seo";
import {
  PLANS_PRICING,
  STAFF_ACCOUNTS,
  TRANSACTION_FEES,
} from "../constant_data";
const EnterprisePlan = () => {
  const [activeSection, setActiveSection] = useState("pricing");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["pricing", "features", "perfect-for"];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      <SEO
        title="xPage Enterprise Plan – Premium eCommerce Solution"
        description="Discover the xPage Enterprise Plan for high-volume merchants and large-scale eCommerce operations. Enjoy the lowest transaction fees, unlimited staff accounts, advanced analytics, and premium support."
        keywords="xPage Enterprise Plan, enterprise ecommerce, high-volume merchants, landing page builder, AI tools, unlimited staff accounts, premium support"
        canonical="https://help.xpage.ai/help/pricing-plans/Enterprise%20Plan"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <div className="mb-6">
          <h1 className="text-systemTitle text-2xl font-semibold">
            Enterprise Plan
          </h1>
          <p className="text-sm text-systemSubTitle mt-1">
            The xPage Enterprise Plan is the ultimate solution for high-volume
            merchants, large-scale eCommerce operations, and brands that need
            maximum performance, customization, and support. With the lowest
            transaction fees, the highest thresholds, and premium-level
            features, it's designed for businesses operating at scale.
          </p>
        </div>

        {/* Pricing Details */}
        <div id="pricing" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-3">
            Pricing
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-crown text-xl text-core mb-2"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-1">
                ${PLANS_PRICING.ENTERPRISE.MONTHLY}
              </h3>
              <p className="text-systemLabel text-sm">per month</p>
            </div>
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-credit-card text-xl text-core mb-2"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-1">
                Card Processing
              </h3>
              <p className="text-systemLabel text-sm">
                {TRANSACTION_FEES.ENTERPRISE.CARD}
              </p>
            </div>
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-money-bill-wave text-xl text-core mb-2"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-1">
                COD Processing
              </h3>
              <p className="text-systemLabel text-sm">
                {TRANSACTION_FEES.ENTERPRISE.COD}
              </p>
            </div>
          </div>
        </div>

        {/* Features Table */}
        <div id="features" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-3">
            What's Included
          </h2>
          <p className="text-systemLabel text-sm mb-3">
            In addition to all the features available on all xPage plans, the
            Enterprise Plan includes:
          </p>

          <div className="bg-systemBackground border border-systemBorder rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-400/10 border-b border-systemBorder">
                  <th className="text-left p-3 text-systemTitle text-sm font-semibold">
                    Feature Category
                  </th>
                  <th className="text-left p-3 text-systemTitle text-sm font-semibold">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Core Features
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    AI-powered online landing page builder, unlimited AI landing
                    pages, unlimited products and collections, mobile-first
                    design, custom domain, 24/7 priority support
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Staff Accounts
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    {STAFF_ACCOUNTS.ENTERPRISE} staff member accounts for large
                    teams and departmental management
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Payment & Checkout
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Multiple gateways (Stripe, Paypal, Bancontact, COD & more),
                    high-converting checkout, the lowest processing fees
                    available ({TRANSACTION_FEES.ENTERPRISE.CARD}% card rate,{" "}
                    {TRANSACTION_FEES.ENTERPRISE.COD}% COD rate)
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Analytics
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Advanced analytics, real-time reports, event tracking, and
                    enterprise-level performance insights
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Marketing Tools
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Full suite of growth tools: abandoned checkout recovery,
                    discount codes, product bundles, referral & affiliate
                    program, unlimited contacts, customer segmentation, advanced
                    campaign targeting
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    International Selling
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Multi-language support, duties & import taxes calculation,
                    and localization for global markets
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Hosting & Security
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Unlimited web hosting, free SSL certificate,
                    enterprise-grade security, PCI-compliant payment processing
                  </td>
                </tr>
                <tr className="hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Threshold System
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Highest threshold levels for transaction fees, starting at
                    $15 and scaling dynamically to $1,600+ for top-performing
                    stores; custom thresholds available upon request
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Who It's For Section */}
        <div id="perfect-for" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-3">
            Who It's For
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-building text-2xl text-core mb-3"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-2">
                Large eCommerce Operations
              </h3>
              <p className="text-systemLabel text-sm">
                Managing multiple markets and high sales volumes across global
                operations.
              </p>
            </div>
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-star text-2xl text-core mb-3"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-2">
                Established Brands
              </h3>
              <p className="text-systemLabel text-sm">
                Requiring advanced customization and enterprise-level
                infrastructure.
              </p>
            </div>
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-gem text-2xl text-core mb-3"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-2">
                Premium Businesses
              </h3>
              <p className="text-systemLabel text-sm">
                Looking for the lowest possible transaction costs and premium
                support.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#pricing"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "pricing"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Pricing
              </a>
            </li>
            <li>
              <a
                href="#features"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "features"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                What's Included
              </a>
            </li>
            <li>
              <a
                href="#perfect-for"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "perfect-for"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Who It's For
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EnterprisePlan;
